import capitalize from 'lodash/capitalize';
import { useState, Suspense, memo, useCallback, useEffect } from 'react';
import { Select, useTheme, Box, FormControl, InputLabel, MenuItem, styled } from '@mui/material';
import { js_beautify } from 'js-beautify';
import { IArgument, IArgumentEmailStrings, IArgumentList, IArgumentObject, IArgumentStrings } from '@tymely/atoms';
import { BaseModal } from '@tymely/components';
import { CodeEditor } from '@tymely/components/CodeEditor';

import { ArgumentFieldProps } from './Layout';

const JsonValue = memo((props: { value: string; onChange?: (value: string) => void }) => {
    const theme = useTheme();
    return (
        <Box width="100%" mb={1}>
            <Suspense>
                <CodeEditor
                    code={props.value}
                    language="json"
                    editorProps={{
                        maxHeight: theme.spacing(50),
                    }}
                    onChange={props.onChange}
                />
            </Suspense>
        </Box>
    );
});

JsonValue.displayName = 'JsonValue';

const StyledValueRendering = styled('span', {
    shouldForwardProp: (prop: string) => !['specialValue'].includes(prop),
})<{
    specialValue?: IArgument['special_value'];
}>`
    color: ${({ specialValue }) => (specialValue ? 'gray' : 'black')};
    font-style: ${({ specialValue }) => (specialValue ? 'italic' : 'normal')};
`;

const toJson = (value: object | string[] | null) => (value !== undefined ? js_beautify(JSON.stringify(value)) : '');

export const ObjectArgument = memo(
    (props: ArgumentFieldProps<IArgumentObject | IArgumentList | IArgumentStrings | IArgumentEmailStrings>) => {
        const [open, setOpen] = useState(false);
        const [json, setJson] = useState(toJson(props.argument.value));
        const [isValid, setIsValid] = useState(true);

        useEffect(() => {
            setJson(toJson(props.argument.value));
        }, [props.argument.value]);

        useEffect(() => {
            try {
                JSON.parse(json || '');
                setIsValid(true);
            } catch {
                setIsValid(false);
            }
        }, [json]);

        const onSubmit = useCallback(
            (json: string) => {
                try {
                    const value = JSON.parse(json);
                    props.onChange?.([{ ...props.argument, value }] as IArgument[]);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                }
            },
            [props.onChange],
        );

        return (
            <FormControl size="small" fullWidth sx={{ position: 'relative', justifyContent: 'center' }}>
                {props.withLabel && <InputLabel>{props.argument.name}</InputLabel>}
                <BaseModal
                    open={open}
                    okLabel="Save"
                    onOk={() => {
                        if (!isValid) {
                            return;
                        }
                        onSubmit(json);
                        setOpen(false);
                    }}
                    disabled={props.disabled || (!isValid ? 'Invalid JSON' : false)}
                    onClose={() => {
                        setJson(toJson(props.argument.value));
                        setOpen(false);
                    }}
                    title={props.argument.title}
                    maxWidth="lg"
                >
                    <JsonValue value={json} onChange={props.disabled ? undefined : setJson} />
                </BaseModal>
                <Select
                    size="small"
                    fullWidth
                    value={json}
                    variant="outlined"
                    displayEmpty
                    renderValue={() => {
                        return (
                            <StyledValueRendering specialValue={props.argument.special_value}>
                                {props.argument.special_value ? capitalize(props.argument.special_value) : json}
                            </StyledValueRendering>
                        );
                    }}
                    open={false}
                    label={props.withLabel ? props.argument.name : ''}
                    onOpen={() => setOpen(true)}
                >
                    <MenuItem value={json}></MenuItem>
                </Select>
            </FormControl>
        );
    },
);

ObjectArgument.displayName = 'ObjectArgument';
